import React, { useMemo, useState } from "react";
import { Empty } from "antd";
import ReactApexChart from "react-apexcharts";
import { useAxisLabelFontStyle } from "../../../Utlis/CharStyle";
import GraphLayout from "../../GraphLayout";

const BarChart = ({
  graphData,
  lengthKeys,
  colourTheme = ["#000", "#fff"],
  graphTitle = "Undefined",
  selectTenByDefault = false,
  classNameCSS,
  units = [],
  dottedline = [],
  grayzoned = false,
}) => {
  const { axisLabelFontStyle, chartRef } = useAxisLabelFontStyle();
  const [filteredLabels, setFilteredLabels] = useState([]);
  const [series, setSeries] = useState([]);

  const filteredGraphData = useMemo(() => {
    if (!graphData || !lengthKeys) return graphData;
    const entries = Object.entries(graphData);
    const slicedEntries = entries.slice(0, lengthKeys + 1);
    return Object.fromEntries(slicedEntries);
  }, [graphData, lengthKeys]);

  if (graphData?.labels?.length < 1 || graphData?.label?.length < 1) {
    return (
      <GraphLayout
        title={graphTitle}
        data={filteredGraphData}
        setFilteredLabels={setFilteredLabels}
        series={series}
        setSeries={setSeries}
        selectTenByDefault={selectTenByDefault}
        colourTheme={colourTheme}
        classNameCSS={classNameCSS}
      >
        <Empty
          style={{
            height: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
          description={
            <span style={{ color: "#FB3748" }}>
              {`No ${graphTitle} data available`}
            </span>
          }
        />
      </GraphLayout>
    );
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    colors: colourTheme,
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        gradientToColors: [grayzoned ? ["#E4E4E7"] : colourTheme[0]],
        inverseColors: false,
      },
      colors: grayzoned ? ["#E4E4E7"] : colourTheme,
    },

    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        colors: ["var(--stroke, #E4E4E7))"],
        fontSize: "12px",
        fontWeight: 400,
      },
    },

    legend: {
      show: true,
      position: "top",
      fontSize: "12px",
      labels: {
        colors: "var(--stroke, #E4E4E7)",
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: axisLabelFontStyle,
      },
      axisBorder: {
        show: true,
        color: "var(--stroke, #E4E4E7)",
      },
      axisTicks: {
        show: true,
        color: "var(--stroke, #E4E4E7)",
      },
    },
    annotations: {
      yaxis: dottedline.map((d) => ({
        y: d.value, // horizontal line position
        borderColor: d.color || "#000",
        strokeDashArray: d.dashArray || 5,
        strokeWidth: d.width || 2,
        label: {
          borderColor: "transparent",
          style: { color: d.color || "#000", background: "transparent" },
          text: d.label || "",
        },
      })),
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        dataLabels: {
          position: "top",
        },
      },
    },
    xaxis: {
      categories: filteredLabels,
      labels: {
        show: true,
        style: axisLabelFontStyle,
      },
      axisBorder: {
        show: true,
        color: "var(--stroke, #E4E4E7)",
      },
      axisTicks: {
        show: true,
        color: "var(--stroke, #E4E4E7)",
      },
    },
    grid: {
      borderColor: "var(--stroke, #E4E4E7)",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      enabled: true,
      y: units.map((unit) => ({
        formatter: function (val) {
          if (val === undefined || val === null) return "";
          return unit === "$" ? `${unit} ${val}` : `${val} ${unit}`;
        },
      })),
    },
  };

  return (
    <GraphLayout
      title={graphTitle}
      data={filteredGraphData}
      setFilteredLabels={setFilteredLabels}
      series={series}
      setSeries={setSeries}
      selectTenByDefault={selectTenByDefault}
      colourTheme={colourTheme}
      classNameCSS={classNameCSS}
    >
      <div ref={chartRef}>
        <ReactApexChart
          height={300}
          options={options}
          series={series}
          type="bar"
        />
      </div>
    </GraphLayout>
  );
};

export default BarChart;
